import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './QuestionsAnswersModal.css';

interface Question {
  id: string;
  question_text: string;
  dimension_id: string;
  dimension_name: string;
  component_id: string;
  component_name: string;
  attribute_id: string;
  attribute_name: string;
  answer_options: AnswerOption[];
  selected_answer_id?: string;
  selected_answer_text?: string;
  current_score?: number;
  weight?: number;
}

interface AnswerOption {
  id: string;
  option_text: string;
  score: number;
}

interface QuestionsAnswersModalProps {
  applicationId: string;
  applicationName: string;
  aprRunId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;  // Optional callback when changes are saved
}

const QuestionsAnswersModal: React.FC<QuestionsAnswersModalProps> = ({
  applicationId,
  applicationName,
  aprRunId,
  isOpen,
  onClose,
  onSave
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<{ [questionId: string]: string }>({});
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && applicationId) {
      loadQuestions();
    }
  }, [isOpen, applicationId, aprRunId]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const data = await apiService.getApplicationQuestions(applicationId, aprRunId);
      setQuestions(data);
    } catch (error) {
    }
    setLoading(false);
  };

  const handleAnswerChange = (questionId: string, answerId: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [questionId]: answerId
    }));
  };

  const handleSave = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      return;
    }

    setSaving(true);
    try {
      // Convert pending changes to API format
      const responses = Object.entries(pendingChanges).map(([questionId, answerId]) => ({
        question_id: questionId,
        answer_option_id: answerId
      }));

      await apiService.submitBulkAssessment(applicationId, aprRunId, responses);
      
      // Reload questions to get updated data
      await loadQuestions();
      
      // Clear pending changes
      setPendingChanges({});
      
      // Check if all questions are now answered
      const totalQuestions = questions.length;
      const answeredQuestions = questions.filter(q => 
        q.selected_answer_id || pendingChanges[q.id]
      ).length;
      
      if (answeredQuestions < totalQuestions) {
        const remaining = totalQuestions - answeredQuestions;
        alert(`✅ Answers saved successfully!\n\n📊 Score Calculation Status:\n• ${answeredQuestions}/${totalQuestions} questions answered\n• ${remaining} questions remaining\n\nNote: Overall scores and recommendations will be calculated automatically once ALL questions are answered.`);
      } else {
        alert('✅ All questions answered! Scores and recommendations have been calculated.');
      }

      // Call onSave callback to notify parent
      if (onSave) {
        onSave();
      }

    } catch (error) {
      alert('Error saving changes. Please try again.');
    }
    setSaving(false);
  };

  const handleSaveAndClose = async () => {
    // Save first if there are pending changes
    if (Object.keys(pendingChanges).length > 0) {
      await handleSave();
    }
    // Always close the modal after saving (or if no changes)
    onClose();
  };

  const handleCancel = () => {
    setPendingChanges({});
    setEditMode(false);
  };

  const toggleDimension = (dimensionId: string) => {
    setExpandedDimensions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dimensionId)) {
        newSet.delete(dimensionId);
      } else {
        newSet.add(dimensionId);
      }
      return newSet;
    });
  };

  const toggleComponent = (componentKey: string) => {
    setExpandedComponents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentKey)) {
        newSet.delete(componentKey);
      } else {
        newSet.add(componentKey);
      }
      return newSet;
    });
  };

  const expandAllDimensions = () => {
    const allDimensions = new Set(sortedQuestions.map(q => q.dimension_id));
    setExpandedDimensions(allDimensions);
    
    const allComponents = new Set(sortedQuestions.map(q => `${q.dimension_id}-${q.component_id}`));
    setExpandedComponents(allComponents);
  };

  const collapseAll = () => {
    setExpandedDimensions(new Set());
    setExpandedComponents(new Set());
  };

  // Sort questions by dimension order as previously instructed
  const sortedQuestions = [...questions].sort((a, b) => {
    const dimensionOrder = [
      'Business Value & Strategic Fit',
      'Business Case',
      'Application Performance', 
      'Application Risk Mitigation',
      'Architectural Maturity'
    ];
    
    const aIndex = dimensionOrder.findIndex(d => a.dimension_name.includes(d.split(' ')[0]));
    const bIndex = dimensionOrder.findIndex(d => b.dimension_name.includes(d.split(' ')[0]));
    
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    
    // Secondary sort by component name, then attribute name
    if (a.component_name !== b.component_name) {
      return a.component_name.localeCompare(b.component_name);
    }
    
    return a.attribute_name.localeCompare(b.attribute_name);
  });

  const getSelectedAnswerForQuestion = (question: Question): string => {
    // Check pending changes first
    if (pendingChanges[question.id]) {
      return pendingChanges[question.id];
    }
    // Fall back to current selection
    return question.selected_answer_id || '';
  };

  // Group questions by dimension and component
  const groupedQuestions = () => {
    const grouped: { [dimensionId: string]: { 
      dimension: Question, 
      components: { [componentId: string]: { component: Question, questions: Question[] } }
    }} = {};

    sortedQuestions.forEach(question => {
      if (!grouped[question.dimension_id]) {
        grouped[question.dimension_id] = {
          dimension: question,
          components: {}
        };
      }

      if (!grouped[question.dimension_id].components[question.component_id]) {
        grouped[question.dimension_id].components[question.component_id] = {
          component: question,
          questions: []
        };
      }

      grouped[question.dimension_id].components[question.component_id].questions.push(question);
    });

    return grouped;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '0',
          width: '90vw',
          maxWidth: '1400px',
          height: '85vh',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '500',
              color: '#1a1a1a'
            }}>
              Questions & Answers
            </h2>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666'
            }}>
              {applicationName}
            </p>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={expandAllDimensions}
              disabled={loading}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                backgroundColor: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.6 : 1
              }}
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              disabled={loading}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                backgroundColor: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: loading ? 0.6 : 1
              }}
            >
              Collapse All
            </button>
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                disabled={loading}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#1976d2',
                  backgroundColor: 'transparent',
                  border: '1px solid #1976d2',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || Object.keys(pendingChanges).length === 0}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: (saving || Object.keys(pendingChanges).length === 0) ? '#90caf9' : '#1976d2',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (saving || Object.keys(pendingChanges).length === 0) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleSaveAndClose}
                  disabled={saving}
                  style={{
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: saving ? '#90caf9' : '#1976d2',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {saving ? 'Saving...' : 'Save & Close'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#666',
                backgroundColor: 'transparent',
                border: '1px solid #ddd',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minWidth: '40px'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="qa-modal-content">
          {loading ? (
            <div className="qa-loading">Loading questions...</div>
          ) : (
            <div className="qa-hierarchy-container">
              {Object.entries(groupedQuestions()).map(([dimensionId, dimensionData]) => {
                const isExpanded = expandedDimensions.has(dimensionId);
                const componentsCount = Object.keys(dimensionData.components).length;
                const questionsCount = Object.values(dimensionData.components).reduce((sum, comp) => sum + comp.questions.length, 0);
                
                return (
                  <div key={dimensionId} className="qa-dimension-section">
                    <div 
                      className="qa-dimension-header"
                      onClick={() => toggleDimension(dimensionId)}
                    >
                      <div className="qa-dimension-info">
                        <span className="qa-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                        <h3 className="qa-dimension-name">{dimensionData.dimension.dimension_name}</h3>
                        <span className="qa-counts">
                          {componentsCount} components • {questionsCount} questions
                        </span>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="qa-dimension-content">
                        {Object.entries(dimensionData.components).map(([componentId, componentData]) => {
                          const componentKey = `${dimensionId}-${componentId}`;
                          const isComponentExpanded = expandedComponents.has(componentKey);
                          
                          return (
                            <div key={componentKey} className="qa-component-section">
                              <div 
                                className="qa-component-header"
                                onClick={() => toggleComponent(componentKey)}
                              >
                                <div className="qa-component-info">
                                  <span className="qa-expand-icon">{isComponentExpanded ? '▼' : '▶'}</span>
                                  <h4 className="qa-component-name">{componentData.component.component_name}</h4>
                                  <span className="qa-component-count">
                                    {componentData.questions.length} questions
                                  </span>
                                </div>
                              </div>
                              
                              {isComponentExpanded && (
                                <div className="qa-component-content">
                                  {componentData.questions.map((question) => {
                                    const selectedAnswerId = getSelectedAnswerForQuestion(question);
                                    const selectedAnswer = question.answer_options.find(opt => opt.id === selectedAnswerId);
                                    const hasChanges = pendingChanges[question.id] !== undefined;
                                    
                                    return (
                                      <div key={question.id} className={`qa-question-card ${hasChanges ? 'qa-question-card--changed' : ''}`}>
                                        <div className="qa-question-header">
                                          <div className="qa-attribute-name">{question.attribute_name}</div>
                                          <div className="qa-question-meta">
                                            <span className="qa-weight-badge">Weight: {question.weight?.toFixed(1) || 'N/A'}</span>
                                            <span className="qa-score-badge">Score: {question.current_score?.toFixed(1) || 'N/A'}</span>
                                          </div>
                                        </div>
                                        
                                        <div className="qa-question-content">
                                          <div className="qa-question-text">{question.question_text}</div>
                                          
                                          <div className="qa-answer-section">
                                            <label className="qa-answer-label">Answer:</label>
                                            {editMode ? (
                                              <div className="qa-answer-edit-container">
                                                <select
                                                  className="qa-answer-select"
                                                  value={selectedAnswerId}
                                                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                >
                                                  <option value="">Select Answer</option>
                                                  {question.answer_options.map(option => (
                                                    <option key={option.id} value={option.id}>
                                                      {option.option_text} (Score: {option.score})
                                                    </option>
                                                  ))}
                                                </select>
                                                
                                                {/* Show all answer options for reference */}
                                                <div className="qa-answer-options-reference">
                                                  <div className="qa-options-title">All Answer Options:</div>
                                                  <div className="qa-options-grid">
                                                    {question.answer_options.map(option => (
                                                      <div 
                                                        key={option.id} 
                                                        className={`qa-option-item ${selectedAnswerId === option.id ? 'qa-option-item--selected' : ''}`}
                                                        onClick={() => handleAnswerChange(question.id, option.id)}
                                                      >
                                                        <div className="qa-option-text">{option.option_text}</div>
                                                        <div className="qa-option-score">Score: {option.score}</div>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="qa-answer-display">
                                                <div className="qa-current-answer">
                                                  {selectedAnswer?.option_text || 'Not answered'}
                                                </div>
                                                {question.current_score && (
                                                  <div className="qa-current-score">Score: {question.current_score}</div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {sortedQuestions.length === 0 && !loading && (
                <div className="qa-empty">
                  No questions found for this application.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsAnswersModal;